import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { useEvento } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Detalle de un evento puntual: /eventos/:slug
 *
 * Muestra cabecera con fecha+hora+lugar, descripción (BlocksRenderer),
 * un CTA de inscripción si la URL está disponible y el evento aún no
 * ha pasado, y enlaces contextuales a la sede, al programa relacionado
 * y a la galería (si los hay).
 */
export function EventoDetallePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: evento, isLoading } = useEvento(slug ?? "");

  if (!slug) return <NotFoundPage />;
  if (isLoading) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando…</p>
      </section>
    );
  }
  if (!evento) return <NotFoundPage />;

  const fechaInicio = new Date(evento.fechaInicio);
  const fechaFin = evento.fechaFin ? new Date(evento.fechaFin) : null;
  const yaPasado = fechaInicio.getTime() < Date.now();

  const fechaInicioTxt = fechaInicio.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const horaInicioTxt = fechaInicio.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const fechaFinTxt = fechaFin
    ? fechaFin.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  const horaFinTxt = fechaFin
    ? fechaFin.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : null;

  const portadaSrc =
    mediaUrl(evento.imagenPortada?.formats?.large?.url) ??
    mediaUrl(evento.imagenPortada?.url);

  const seoDescription =
    evento.descripcion?.[0]?.type === "paragraph"
      ? evento.descripcion[0].children[0]?.text?.slice(0, 160) ?? evento.titulo
      : evento.titulo;

  return (
    <>
      <Helmet>
        <title>{evento.titulo} — Campus EMJC</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <article className="container-emjc py-20">
        <nav aria-label="Migas de pan" className="text-sm text-gray-700">
          <Link to="/eventos" className="hover:text-brand-orange-deep hover:underline">
            Eventos
          </Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span aria-current="page">{evento.titulo}</span>
        </nav>

        <header className="mt-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            {yaPasado ? "Evento pasado" : "Próximo evento"}
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
            {evento.titulo}
          </h1>

          <dl className="mt-6 grid max-w-2xl gap-x-8 gap-y-3 text-base text-gray-700 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-widest text-brand-ink">
                Fecha
              </dt>
              <dd className="mt-1">
                <time dateTime={evento.fechaInicio}>
                  {fechaInicioTxt} · {horaInicioTxt}
                </time>
                {fechaFinTxt && (
                  <>
                    <br />
                    <span className="text-sm">
                      Hasta {fechaFinTxt} · {horaFinTxt}
                    </span>
                  </>
                )}
              </dd>
            </div>
            {(evento.lugar || evento.sede) && (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-widest text-brand-ink">
                  Lugar
                </dt>
                <dd className="mt-1">
                  {evento.lugar ?? evento.sede?.nombreLugar ?? evento.sede?.ciudad}
                  {evento.sede?.ciudad &&
                    evento.lugar &&
                    evento.lugar !== evento.sede.ciudad && (
                      <span className="block text-sm text-gray-700">
                        {evento.sede.ciudad}
                      </span>
                    )}
                </dd>
              </div>
            )}
          </dl>

          {evento.urlInscripcion && !yaPasado && (
            <div className="mt-8">
              <a
                href={evento.urlInscripcion}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-brand-paper shadow-md transition hover:bg-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
              >
                Inscribirse al evento
                <span aria-hidden="true">→</span>
              </a>
            </div>
          )}
        </header>

        {portadaSrc && (
          <figure className="mt-10 overflow-hidden rounded-2xl">
            <img
              src={portadaSrc}
              alt={evento.imagenPortada?.alternativeText ?? ""}
              className="w-full"
            />
            {evento.imagenPortada?.alternativeText && (
              <figcaption className="mt-2 text-sm text-gray-700">
                {evento.imagenPortada.alternativeText}
              </figcaption>
            )}
          </figure>
        )}

        {evento.descripcion && evento.descripcion.length > 0 && (
          <div className="mt-10">
            <BlocksRenderer blocks={evento.descripcion} />
          </div>
        )}

        {/* Bloque de relaciones contextuales */}
        {(evento.programa || evento.galeria) && (
          <aside className="mt-16 rounded-2xl border border-gray-300 bg-white p-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
              También te puede interesar
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {evento.programa && (
                <li>
                  Programa relacionado:{" "}
                  <Link
                    to={`/programas/${evento.programa.slug}`}
                    className="font-semibold text-brand-ink underline underline-offset-4 hover:text-brand-orange-deep"
                  >
                    {evento.programa.nombre}
                  </Link>
                </li>
              )}
              {evento.galeria && yaPasado && (
                <li>
                  Galería de fotos:{" "}
                  <Link
                    to={`/galeria/${evento.galeria.slug}`}
                    className="font-semibold text-brand-ink underline underline-offset-4 hover:text-brand-orange-deep"
                  >
                    {evento.galeria.titulo}
                  </Link>
                </li>
              )}
            </ul>
          </aside>
        )}

        <div className="mt-12">
          <Link
            to="/eventos"
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep hover:underline"
          >
            ← Volver a todos los eventos
          </Link>
        </div>
      </article>
    </>
  );
}
